import { z } from 'zod';

/**
 * The automation-rule contract, shared between the backend (which validates
 * `POST /rules` and `PATCH /rules/:id` against it) and the Clinic Portal (which
 * derives its types from it rather than restating them).
 *
 * Pure Zod, zero other imports, so nothing backend-shaped leaks into browser
 * bundles — same constraint as `lib/list.ts`.
 *
 * These rules are persisted as JSON and later *executed* by
 * `backend/src/services/rules/{conditions,actions}.ts`, so this schema is the
 * boundary between user input and code that sends mail, writes labels and spends
 * quota. The TS unions in those two files mirror this schema; keep them in
 * lockstep, or better, derive them from `z.infer` here.
 */

export const RuleTriggerTypeSchema = z.enum(['on_ingest', 'scheduled']);

export const RuleConditionOperatorSchema = z.enum([
  'equals',
  'notEquals',
  'contains',
  'in',
  'gt',
  'lt',
  'matchesDomain',
]);

/**
 * `field` stays an open string: the evaluator resolves both real fact keys and
 * derived ones (`senderDomain`, `hoursSinceLastActivity`), and an unknown field
 * simply fails to match rather than erroring.
 */
export const RuleConditionSchema = z.object({
  field: z.string(),
  operator: RuleConditionOperatorSchema,
  value: z.union([z.string(), z.number(), z.array(z.string())]),
});

/**
 * `add_label`/`remove_label` accept both `tagId` (the mail-tags shape, issue #44)
 * and `labelId` (a raw provider label id, pre-redesign) as optional. Both shapes
 * are live: rules saved before the redesign still carry `labelId` in their stored
 * JSON, and expand/contract means that JSON is never rewritten underneath them.
 *
 * An action with neither is accepted here and fails at execution — the executor
 * returns 'Action has no tagId or labelId'. Every *other* action's params are
 * required, because their executors write the value straight into the database
 * with no guard.
 */
export const RuleActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('add_label'),
    params: z.object({
      tagId: z.number().int().positive().optional(),
      labelId: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal('remove_label'),
    params: z.object({
      tagId: z.number().int().positive().optional(),
      labelId: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal('create_task'),
    params: z.object({
      title: z.string().optional(),
      assignedTo: z.number().int().positive().optional(),
      priority: z.number().int().optional(),
      dueInHours: z.number().optional(),
    }),
  }),
  z.object({
    type: z.literal('link_contact'),
    params: z.object({ contactId: z.number().int().positive() }),
  }),
  z.object({
    type: z.literal('set_category'),
    params: z.object({ categoryId: z.number().int().positive() }),
  }),
  z.object({
    type: z.literal('set_priority'),
    params: z.object({ weight: z.number() }),
  }),
  z.object({
    type: z.literal('auto_reply'),
    params: z.object({ cannedResponseId: z.number().int().positive() }),
  }),
]);

export const RuleActionTypeSchema = z.enum([
  'add_label',
  'remove_label',
  'create_task',
  'link_contact',
  'set_category',
  'set_priority',
  'auto_reply',
]);

/**
 * `POST /rules`.
 *
 * The arrays are intentionally not `.min(1)`: `RulesService.create` already rejects
 * empty conditions/actions with its own 400 and message, and duplicating that here
 * would change the response shape clients currently get for that case.
 */
export const CreateRuleSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  triggerType: RuleTriggerTypeSchema.optional(),
  conditions: z.array(RuleConditionSchema),
  actions: z.array(RuleActionSchema),
  priority: z.number().int().optional(),
  stopOnMatch: z.boolean().optional(),
  isEnabled: z.boolean().optional(),
});

/**
 * `PATCH /rules/:id`. Every field optional — the handler applies only the keys
 * that are present, so `{}` is a valid no-op patch.
 */
export const UpdateRuleSchema = CreateRuleSchema.partial();

export type RuleTriggerType = z.infer<typeof RuleTriggerTypeSchema>;
export type RuleConditionOperator = z.infer<typeof RuleConditionOperatorSchema>;
export type RuleConditionDef = z.infer<typeof RuleConditionSchema>;
export type RuleActionDef = z.infer<typeof RuleActionSchema>;
export type RuleActionType = z.infer<typeof RuleActionTypeSchema>;
export type CreateRuleBody = z.infer<typeof CreateRuleSchema>;
export type UpdateRuleBody = z.infer<typeof UpdateRuleSchema>;

/**
 * True when an action carries every param its executor requires.
 *
 * Shared with the Clinic Portal's rule form so the UI can block an incomplete
 * action before it is sent, rather than relying on a 400. `add_label`/`remove_label`
 * are the deliberate exception — see `RuleActionSchema`.
 */
export function isActionComplete(action: { type: string; params: Record<string, unknown> }) {
  return RuleActionSchema.safeParse(action).success;
}
